import type { HTMLAttributes, ReactNode, Ref } from "react";
import { Switch as BaseSwitch } from "@base-ui/react/switch";
import classNames from "classnames";
import { SpinnerIcon } from "@icons";
import { ariaLabelledbyProps } from "@design-system/formControl/ariaLabelledbyProps";
import type { CheckedControlProps } from "@design-system/formControl/formControl.types";
import styles from "./Switch.module.scss";

// Visual source of truth: docs/specs/2026-09-28-switch-figma-rework.md.
// Base UI renders a hidden input sibling after the root, so positional
// selectors must use :not(:first-child), never the adjacent-sibling combinator.

export type SwitchSize = "sm" | "md";

/**
 * DOM props a wrapping trigger (tooltip, popover) clones onto its child: event
 * handlers and attributes land on the focusable root.
 */
type SwitchRootDomProps = Omit<
    HTMLAttributes<HTMLElement>,
    | "children"
    | "className"
    | "defaultChecked"
    | "onBlur"
    | "onChange"
    | "role"
    | "style"
    | `aria-${string}`
>;

export interface SwitchProps
    extends CheckedControlProps<HTMLElement>, SwitchRootDomProps {
    label?: ReactNode;
    /** Keeps `label` as the accessible name but hides it visually. */
    labelHidden?: boolean;
    /** "start" renders the label first in a full-width row with the track pinned to the end. */
    labelPlacement?: "end" | "start";
    /** Id of a visible element elsewhere that names the switch. */
    "aria-labelledby"?: string;
    "aria-label"?: string;
    /** Shows a spinner in the thumb and ignores input until the saved value arrives. */
    loading?: boolean;
    /** `sm` is a 28x16 track, `md` a 36x20 track. */
    size?: SwitchSize;
    /** The hidden native input, for form libraries that drive focus or read the node. */
    inputRef?: Ref<HTMLInputElement>;
    className?: string;
    "data-testid"?: string;
    /** Playground-only: statically render an interaction state. */
    "data-forced-state"?: "hover" | "pressed" | "focus";
}

/**
 * On/off control on the standard checked API. The Base UI root is the whole
 * row, so a label click toggles and the accessible name comes from the label.
 * `ref` points at that focusable `span role="switch"`.
 */
export const Switch = ({
    checked,
    onCheckedChange,
    onBlur,
    name,
    ref,
    invalid = false,
    disabled = false,
    readOnly = false,
    required = false,
    label,
    labelHidden = false,
    labelPlacement = "end",
    "aria-labelledby": ariaLabelledby,
    "aria-label": ariaLabel,
    loading = false,
    size = "md",
    inputRef,
    className,
    "data-testid": dataTestid,
    "data-forced-state": forcedState,
    ...rootDomProps
}: Readonly<SwitchProps>) => (
    <BaseSwitch.Root
        {...rootDomProps}
        ref={ref}
        inputRef={inputRef}
        name={name}
        checked={checked}
        onCheckedChange={(nextChecked) => onCheckedChange(nextChecked)}
        onBlur={onBlur}
        disabled={disabled}
        readOnly={readOnly || loading}
        aria-required={required || undefined}
        aria-invalid={invalid || undefined}
        aria-busy={loading || undefined}
        aria-readonly={readOnly || undefined}
        aria-label={ariaLabel}
        {...ariaLabelledbyProps(ariaLabelledby)}
        className={classNames(
            styles.switch,
            styles.interactive,
            labelPlacement === "start" && styles.labelStart,
            className,
        )}
        data-size={size}
        data-loading={loading ? "" : undefined}
        data-testid={dataTestid}
        data-forced-state={forcedState}
    >
        <span className={styles.track}>
            <BaseSwitch.Thumb className={styles.thumb}>
                <SpinnerIcon aria-hidden="true" className={styles.spinner} />
            </BaseSwitch.Thumb>
        </span>
        {label !== undefined && (
            <span
                className={classNames(
                    styles.label,
                    labelHidden && styles.labelHidden,
                )}
            >
                {label}
            </span>
        )}
    </BaseSwitch.Root>
);
